import React from 'react';
import { RentalStepProps } from 'rental/app/steps';
import { RentalPageTitleHelmet } from 'rental/components/PageTitleHelmet';
import { PageContainer } from 'shared/components/PageContainer';
import { NextButton, SubmitButtons } from 'shared/components/SubmitButtons';
import { PageHeading } from 'shared/components/typography';
import { useMediaBreakpoints } from 'shared/media';
import { EmptyApplicationFlush } from '../../app/EmptyApplication';
import { App_PortalFragment, Cart_ApplicationFragment, useClearApplicationWarningsMutation } from '../../generated.graphql';
import styles from './Cart.module.scss';
import { CartStack } from './CartStack';
import { CartTable } from './CartTable';
import { UnderstoodMinPeriods, useUnderstoodMinPeriodsState } from './UnderstandMinRental';
import { getContinueShoppingUrl } from './resolvers';
 

interface Props extends RentalStepProps {
  application: Cart_ApplicationFragment;
  portal: App_PortalFragment
}


export const Cart = (props: Props) => {
  const { application, portal } = props;
  const media = useMediaBreakpoints();
  const [updateWarningsResult, executeClearWarnings] = useClearApplicationWarningsMutation();

  const clearWarnings = async () => {
    // don't bother calling if we don't have mixed rates
    // if (application.hasMixedRates) {
    //   await executeClearWarnings({ input: { portalSlug: portal.slug }});
    // }
  }

  // State for the "I understand the min rental periods I have selected"  
  const understoodMinRental = useUnderstoodMinPeriodsState(props.application);
  const [showUnderstoodMinRentalError, setShowUnderstoodMinRentalError] = React.useState(false);
  const [cartValidated, setCartValidated] = React.useState(false);
  const understoodMinRentalState: UnderstoodMinPeriods = {
    ...understoodMinRental,
    validated: cartValidated,
    invalid: showUnderstoodMinRentalError,
    itemsValid: application.items?.every(i => i.minMonthsConfirmed) ?? true
  };

  const continueShoppingUrl = getContinueShoppingUrl(props);
  
  if ((application.items?.length || 0) > 0) {
    const hasImage = (application.items?.filter(item => item.imageUrl).length || 0) > 0;
    const isNotInStore = !application.inStore;
    const firstItem = application.items ? application.items[0] : undefined;
    return (
      <>
        <PageContainer extraClassName={styles.Cart}>
          <RentalPageTitleHelmet>Cart</RentalPageTitleHelmet>
          <PageHeading className="mb-4">
            <span className="mr-4">You have {application.items?.length} item{application.items?.length === 1 ? '' : 's'} in your cart</span>
          </PageHeading>

          {/* {application.hasMixedRates && (
            <div className="alert alert-warning text-center my-3" role="alert">
              Please note: All items on an application must have the same minimum rental period, therefore we have updated the items in your cart to reflect this.
              <button type="button" className="close" disabled={updateWarningsResult?.fetching} aria-label="Close" onClick={clearWarnings}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          )} */}

          {(media.mdGTE) ? 
            <CartTable 
              application={application} 
              portal={props.portal} 
              className="mb-5" 
              understoodMinRental={understoodMinRentalState} 
              columns={{
                image: hasImage, 
                condition: isNotInStore, 
                quantity: isNotInStore || ((application.items?.length ?? 0) >= 2) || firstItem?.qty !== 1, 
                remove: isNotInStore,
              }}
            /> 
          : 
            <CartStack 
              application={application} 
              portal={props.portal} 
              className="mb-5" 
              remove={isNotInStore}
              understoodMinRental={understoodMinRentalState} 
            />
          }
          {/* {application.protectionPlan && 
            <ProtectionPlan application={application} className="shadow px-2 py-3 mb-4"/>
          } */}
          {application.inStore &&
            <p>
              <em>Note:</em> If you would like to change your application please contact the retailer.
            </p>
          }
        </PageContainer>
        
        <PageContainer>
          {application.validation.cart?.map((e, ei) => (
            <React.Fragment key={ei}>
              {e?.messages.map((m, mi) => (
                <div key={mi} className="alert alert-danger text-center my-3">{m}</div>
              ))}
            </React.Fragment>
          ))}
          {understoodMinRentalState.validated && (understoodMinRentalState.invalid || !understoodMinRentalState.itemsValid) && 
            <div className="alert alert-danger text-center my-3">
              {!understoodMinRentalState.itemsValid ? (application.rateOptions.length > 1 ? "Please select a minimum rental period for your cart above." : "Please select a minimum rental period for each item in your cart.") : 
                understoodMinRentalState.invalid ? "Please review and agree to the minimum rental periods you have selected in the above checkbox." : ""
              }
            </div>
          }
          
          <SubmitButtons
            xsStack={true}
            left={!application.inStore && continueShoppingUrl &&
              <a className="btn btn-outline-secondary btn-wide w-100 w-sm-auto" href={continueShoppingUrl}>Continue Shopping</a>
            }
            right={
              <NextButton
                className="w-100 w-sm-auto"
                onClick={async () => {
                  let valid = true;
                  setCartValidated(true);

                  // the graph ensures min months are confirmed by default on portals that don't require per item rate selection
                  if (!application.items?.every(i => i.minMonthsConfirmed)) {
                    valid = false;
                  }

                  if (application.portal.requireMinMonthsConfirmation && !understoodMinRental.checked) {
                    setShowUnderstoodMinRentalError(true);
                    valid = false;
                  } else {
                    setShowUnderstoodMinRentalError(false);
                  }
                  
                  if (valid) {
                    // This is a combined mutation that clears warnings and records an interaction
                    await clearWarnings();
                    props.onSubmitSuccessful();
                  }
                }}
                disabled={(application.validation.cart !== null)}
              >
                Begin Application
              </NextButton>
            }
          />
        </PageContainer>
      </>
    )
  }
  
  return (
    <EmptyApplicationFlush portal={props.portal}/>
  )
};

